/**
 * runtimeEnvVars.js
 *
 *   IMPORTANT: This file must stay CommonJS. `internals/webpack/webpack.prod.babel.js`
 *   gives it a dedicated `runtimeEnvVars.js` chunk (splitChunks cacheGroup
 *   `config`) so a deploy can swap the file, and CJS is what keeps
 *   `concatenateModules` from scope-hoisting it into another chunk.
 *
 *   Assign to `exports.<NAME>` one at a time rather than `module.exports = {…}`.
 *   cjs-module-lexer only recognises identifier shorthand in an object literal,
 *   so the old form exposed 5 of these names and hid the rest (NODE_ENV, the
 *   POSTHOG vars, both _URL getters). Storybook's Vite dev server relies on that
 *   lexer to synthesise named exports, so `import { NODE_ENV } from
 *   'runtimeEnvVars'` failed in the browser while webpack and Jest were fine.
 */

const { domainMeta, getHireGoOrigin } = require('utils/domainMeta');

// Tenants with a dedicated hire cluster pin the wx release channel via Helm
// ConfigMap injection (see apps/hire/helm/tenants/aimbridge-na/values.yaml).
// REACT_APP_* vars are baked at build time by Webpack, so pod env overrides
// have no effect here. This map provides a hostname-derived browser-layer
// fallback for all custom-deploy tenants (both those with and without a
// dedicated hire cluster, including UPS which runs on shared infra).
const TENANT_WX_RELEASE_CHANNELS = {
  aimbridge: 'aimbridge',
  ups: 'ups',
};

// Locals first: the two URLs below are built from them. They used to be getters
// reading `this.REACT_APP_WX_*`, which an object literal allowed and separate
// `exports.` assignments do not.
const WX_COPILOT_VERSION = 'v8';
const WX_COPILOT_RELEASE_CHANNEL =
  process.env.REACT_APP_WX_COPILOT_RELEASE_CHANNEL ||
  TENANT_WX_RELEASE_CHANNELS[domainMeta.tenant] ||
  (domainMeta.environment === 'prod' ? 'stable' : 'main');

const WX_NAVBAR_VERSION = 'v3';
const WX_NAVBAR_RELEASE_CHANNEL =
  process.env.REACT_APP_WX_NAVBAR_RELEASE_CHANNEL ||
  TENANT_WX_RELEASE_CHANNELS[domainMeta.tenant] ||
  (domainMeta.environment === 'prod' ? 'stable' : 'main');

exports.REACT_APP_MONOLITH_BASE_URL = domainMeta.monolithOrigin;
exports.REACT_APP_GLOBAL_API_BASE_URL = `${domainMeta.monolithOrigin}/api_self_serve/v1`;
exports.REACT_APP_GLOBAL_API_BASE_URL_V2 = `${domainMeta.monolithOrigin}/api_self_serve/v2`;
exports.REACT_APP_LANDING_PAGE_URL = `${domainMeta.monolithOrigin}/users/sign_in`;
exports.REACT_APP_SIGN_OUT_REDIRECT_URL = `${domainMeta.monolithOrigin}/users/sign_in`;

exports.REACT_APP_WX_COPILOT_VERSION = WX_COPILOT_VERSION;
exports.REACT_APP_WX_COPILOT_RELEASE_CHANNEL = WX_COPILOT_RELEASE_CHANNEL;
exports.REACT_APP_WX_COPILOT_URL =
  process.env.REACT_APP_WX_COPILOT_URL ||
  `https://ftn-shared-components.fountain.com/wx-copilot/${WX_COPILOT_VERSION}/release/${WX_COPILOT_RELEASE_CHANNEL}/wx-copilot.umd.js`;

exports.REACT_APP_WX_NAVBAR_VERSION = WX_NAVBAR_VERSION;
exports.REACT_APP_WX_NAVBAR_RELEASE_CHANNEL = WX_NAVBAR_RELEASE_CHANNEL;
exports.REACT_APP_WX_NAVBAR_URL =
  process.env.REACT_APP_WX_NAVBAR_URL ||
  `https://ftn-shared-components.fountain.com/wx-navbar/${WX_NAVBAR_VERSION}/release/${WX_NAVBAR_RELEASE_CHANNEL}/wx-navbar.umd.js`;

exports.REACT_APP_WX_LAUNCH_DARKLY_CLIENT_SIDE_ID =
  process.env.REACT_APP_WX_LAUNCH_DARKLY_CLIENT_SIDE_ID ||
  domainMeta.launchDarklyClientSideId;
// Path prefix the WX employer app is served under, relative to the account's
// wx_domain. The WX navbar/copilot build every relative link as
// wx_domain + this + pathname, so it has to match how WX is actually deployed.
// Deployed environments give the employer and worker apps their own hostnames
// (employer.*/portal.*), so both sit at the root and this stays empty. A
// single-origin deployment (the RWX sandboxes) separates them by prefix instead
// and must set '/employer'.
exports.REACT_APP_WX_EMPLOYER_BASE_PATH =
  process.env.REACT_APP_WX_EMPLOYER_BASE_PATH || '';
exports.NODE_ENV = process.env.NODE_ENV;
exports.REACT_APP_FAKER_SEED = process.env.REACT_APP_FAKER_SEED;
exports.REACT_APP_HIRE_GO_BASE_URL =
  process.env.REACT_APP_HIRE_GO_BASE_URL ||
  getHireGoOrigin(domainMeta.environment);
exports.REACT_APP_POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
exports.REACT_APP_POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;
exports.REACT_APP_POSTHOG_UI_HOST = process.env.REACT_APP_POSTHOG_UI_HOST;
exports.REACT_APP_POSTHOG_DEBUG = process.env.REACT_APP_POSTHOG_DEBUG;
exports.REACT_APP_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
exports.REACT_APP_SENTRY_TRACES_SAMPLE_RATE =
  process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;
exports.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE =
  process.env.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE;
exports.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE =
  process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE;
exports.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE =
  process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE;
// The build's full 40-character commit sha, which the build script derives from
// SOURCE_VERSION. Sentry names a release by it and keys uploaded source maps to
// the same name, so the two must be the same string.
exports.COMMIT_SHA = process.env.COMMIT_SHA;
